import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import toast from 'react-hot-toast';

const OTP_LENGTH = 6;

export default function LoginPage() {
  const [step,     setStep]     = useState('email'); // 'email' | 'otp'
  const [email,    setEmail]    = useState('');
  const [otp,      setOtp]      = useState('');
  const [loading,  setLoading]  = useState(false);
  const { sendOtp, verifyOtp } = useAuthStore();
  const navigate  = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendOtp(email);
      toast.success('OTP sent to your email');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      toast.error(`Enter all ${OTP_LENGTH} digits`);
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(email, otp);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setStep('email');
    setOtp('');
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await sendOtp(email);
      toast.success('OTP resent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-page)', padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo.png" alt="Logo" style={{ width: 300, marginBottom: 8 }} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}></h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>Domo → Power BI Migration Tool</p>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--bg-card)', border: '0.5px solid var(--border)',
          borderRadius: 'var(--radius-xl)', padding: 32,
        }}>
          {step === 'email' ? (
            <>
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>Sign in</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 20 }}>
                Enter your email to receive a one-time passcode.
              </p>

              <form onSubmit={handleSendOtp}>
                <FormGroup label="Email address" style={{ marginBottom: 24 }}>
                  <StyledInput
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com" required autoFocus
                  />
                </FormGroup>

                <SubmitButton loading={loading} label="Send OTP" loadingLabel="Sending…" />
              </form>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>Enter OTP</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 20 }}>
                We sent a one-time passcode to <strong>{email}</strong>.
              </p>

              <form onSubmit={handleVerifyOtp}>
                <FormGroup label="One-time passcode" style={{ marginBottom: 20 }}>
                  <OtpInput value={otp} onChange={setOtp} length={OTP_LENGTH} disabled={loading} autoFocus />
                </FormGroup>

                <SubmitButton loading={loading} label="Verify & Sign in" loadingLabel="Verifying…" style={{ marginBottom: 14 }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                  <LinkButton onClick={handleChangeEmail} disabled={loading}>Change email</LinkButton>
                  <LinkButton onClick={handleResendOtp} disabled={loading}>Resend OTP</LinkButton>
                </div>
              </form>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

function FormGroup({ label, children, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, ...style }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</label>
      {children}
    </div>
  );
}

function StyledInput({ style, ...props }) {
  return (
    <input
      style={{
        padding: '9px 12px', border: '0.5px solid var(--border-med)',
        borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)',
        color: 'var(--text-primary)', fontSize: 13, outline: 'none',
        fontFamily: 'var(--font-sans)', width: '100%', transition: 'border .15s',
        ...style,
      }}
      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
      onBlur={e  => e.target.style.borderColor = 'var(--border-med)'}
      {...props}
    />
  );
}

function OtpInput({ value, onChange, length = 6, disabled, autoFocus }) {
  const inputsRef = React.useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const setDigit = (index, char) => {
    const next = value.split('');
    next[index] = char;
    onChange(next.join('').slice(0, length));
  };

  const focusInput = (index) => {
    const el = inputsRef.current[index];
    if (el) el.focus();
  };

  const handleChange = (index, raw) => {
    const char = raw.replace(/\D/g, '').slice(-1);
    setDigit(index, char);
    if (char && index < length - 1) focusInput(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        setDigit(index, '');
      } else if (index > 0) {
        setDigit(index - 1, '');
        focusInput(index - 1);
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    focusInput(Math.min(pasted.length, length - 1));
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={el => (inputsRef.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          style={{
            width: 44, height: 48, textAlign: 'center', fontSize: 18, fontWeight: 600,
            border: `0.5px solid ${digit ? 'var(--accent)' : 'var(--border-med)'}`,
            borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)',
            color: 'var(--text-primary)', outline: 'none',
            fontFamily: 'var(--font-sans)', transition: 'border .15s',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = digit ? 'var(--accent)' : 'var(--border-med)'}
        />
      ))}
    </div>
  );
}

function SubmitButton({ loading, label, loadingLabel, style }) {
  return (
    <button
      type="submit" disabled={loading}
      style={{
        width: '100%', padding: '10px', borderRadius: 'var(--radius-md)',
        background: loading ? 'var(--border)' : 'var(--accent)',
        color: '#fff', border: 'none', fontSize: 14, fontWeight: 600,
        cursor: loading ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 8, transition: 'all .15s',
        ...style,
      }}
    >
      {loading && <Spinner />}
      {loading ? loadingLabel : label}
    </button>
  );
}

function LinkButton({ onClick, disabled, children }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      style={{
        background: 'none', border: 'none', padding: 0,
        color: 'var(--accent)', cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 'inherit', fontFamily: 'var(--font-sans)',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Spinner() {
  return <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />;
}
